import { Component } from '@theme/component';
import { sectionRenderer, morphSection } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

class RecentlyViewedProductsSection extends Component {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#load();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.hydrated === 'true') return;

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  async #load() {
    const { sectionId, searchUrl, currentProductId, maxProducts } = this.dataset;

    if (!sectionId || !searchUrl) {
      this.#hide();
      return;
    }

    let viewedProducts = RecentlyViewed.getProducts();

    if (currentProductId) {
      viewedProducts = viewedProducts.filter(
        /** @param {string} id */ (id) => String(id) !== String(currentProductId)
      );
    }

    if (viewedProducts.length === 0) {
      this.#hide();
      return;
    }

    const limit = parseInt(maxProducts ?? '', 10) || 4;
    viewedProducts = viewedProducts.slice(0, limit);

    try {
      const url = new URL(searchUrl, location.origin);
      url.searchParams.set(
        'q',
        viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR ')
      );
      url.searchParams.set('resources[type]', 'product');

      this.#activeFetch?.abort();
      this.#activeFetch = new AbortController();

      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);

      if (!html?.trim().length) {
        this.#hide();
        return;
      }

      morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
    } catch (error) {
      console.error('Recently viewed products error:', error);
      this.#hide();
    }
  }

  #hide() {
    const section = this.closest('.shopify-section');
    if (section instanceof HTMLElement) {
      section.style.display = 'none';
    } else {
      this.style.display = 'none';
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProductsSection);
}
